// Схлопывание подряд идущих уведомлений с одинаковым `(type, groupKey)` в окне
// 30 минут: «3 артефакта по одному материалу» — одна строка со счётчиком, а не
// три. Чистая функция, без I/O: решение «показать группу или items по
// отдельности» принимает UI по `count > 1`.
//
// ПОЧЕМУ В `packages/shared`, А НЕ НА СЕРВЕРЕ. Функция родилась в
// `apps/server/src/modules/notifications/_internal/aggregator.ts` и пролежала
// там без единого вызова: список уведомлений живёт на клиенте и читает строки
// из PowerSync напрямую, а импортировать код сервера фронт не может
// (`rules/module-boundaries.md`). Половинка была написана не на той стороне —
// поэтому перенесена сюда, откуда её видят обе.
//
// Порядок входа — строго DESC по `createdAt` (как отдаёт
// `useNotificationsListQuery`: `ORDER BY created_at DESC`). На неотсортированном
// входе серия рвётся раньше времени и группы получаются мельче — не падение, но
// молчаливо неверный результат.

const AGGREGATION_WINDOW_MS = 30 * 60 * 1000;

export interface AggregateInput {
	id: string;
	type: string;
	/**
	 * Поле именно ОПЦИОНАЛЬНОЕ, а не `string | null | undefined`: это разные
	 * вещи. TypeScript проверяет наличие свойства отдельно от его типа (и
	 * делает это независимо от `exactOptionalPropertyTypes`), поэтому модель с
	 * `groupKey?: string | null` не подошла бы под обязательное поле, даже
	 * включающее `undefined`.
	 *
	 * По смыслу «ключа нет» приходит по-разному — колонка БД отдаёт `null`,
	 * необязательное поле клиентской модели просто отсутствует, — а ведёт себя
	 * одинаково: уведомление не схлопывается.
	 */
	groupKey?: string | null;
	/**
	 * ISO-строка или `Date`. Оба варианта намеренно: сервер держит `Date`,
	 * клиент получает из SQLite строку, и заставлять одну из сторон
	 * конвертировать значило бы плодить обёртку на ровном месте.
	 */
	createdAt: string | Date;
}

export interface AggregatedNotificationGroup<T extends AggregateInput> {
	representative: T;
	count: number;
	items: T[];
}

function toMs(value: string | Date): number {
	return value instanceof Date ? value.getTime() : new Date(value).getTime();
}

/**
 * @param items уведомления, отсортированные DESC по `createdAt`
 * @returns группы в том же порядке (DESC по `createdAt` представителя)
 */
export function aggregateNotifications<T extends AggregateInput>(
	items: T[],
): AggregatedNotificationGroup<T>[] {
	if (items.length === 0) return [];

	const groups: AggregatedNotificationGroup<T>[] = [];
	let current: AggregatedNotificationGroup<T> | null = null;

	for (const item of items) {
		// Без `groupKey` схлопывать не с чем: уведомление всегда своей строкой.
		// Оно ещё и РАЗРЫВАЕТ текущую серию — иначе две группы, разделённые
		// одиночным уведомлением, склеились бы в одну через его голову, и
		// порядок ленты перестал бы соответствовать времени.
		if (!item.groupKey) {
			if (current) groups.push(current);
			groups.push({ representative: item, count: 1, items: [item] });
			current = null;
			continue;
		}

		if (
			current &&
			current.representative.type === item.type &&
			current.representative.groupKey === item.groupKey &&
			toMs(current.representative.createdAt) - toMs(item.createdAt) <= AGGREGATION_WINDOW_MS
		) {
			current.items.push(item);
			current.count++;
		} else {
			if (current) groups.push(current);
			current = { representative: item, count: 1, items: [item] };
		}
	}
	if (current) groups.push(current);
	return groups;
}
